import React, { useId } from 'react';
import { Search, Copy, ChevronLeft, ChevronRight, Clock, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Badge, ErrorState, IconButton } from '@/components/primitives';
import { copyText, iocProblem, iocTypeLabel } from './adminApi';

/**
 * Shared building blocks for the admin tabs. Every tab uses the same
 * template: AdminSection (heading, count, one-line description, primary
 * action), then a Toolbar (search left, filters, secondary actions), then
 * the table or panels. Styles: styles/admin-console.css (.adm-*).
 */
export function AdminSection({ title, count, description, actions, children, id }) {
  const headingId = useId();
  return (
    <section className="adm-section" aria-labelledby={id || headingId}>
      <div className="adm-section-head">
        <div className="adm-section-titles">
          <h2 id={id || headingId} className="adm-section-title">
            {title}
            {count != null && <span className="adm-section-count">{count}</span>}
          </h2>
          {description && <p className="adm-section-desc">{description}</p>}
        </div>
        {actions && <div className="adm-section-actions">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export function Toolbar({ children, label }) {
  return (
    <div className="adm-toolbar" role={label ? 'group' : undefined} aria-label={label}>
      {children}
    </div>
  );
}

export function SearchField({ value, onChange, label, placeholder, className = '', testId }) {
  const id = useId();
  return (
    <div className={`adm-search ${className}`}>
      <label htmlFor={id} className="sr-only">{label}</label>
      <Search className="adm-search-icon" aria-hidden="true" />
      <input
        id={id}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || label}
        className="adm-input adm-search-input"
        data-testid={testId}
      />
    </div>
  );
}

/**
 * Single-choice filter as toggle buttons (aria-pressed), e.g. status or type.
 * options: [{ value, label, count?, icon? }]
 */
export function FilterChips({ label, options, value, onChange, testIdPrefix }) {
  return (
    <div className="adm-chips" role="group" aria-label={label}>
      {options.map((o) => {
        const active = value === o.value;
        const Icon = o.icon;
        return (
          <button
            key={String(o.value)}
            type="button"
            className="adm-chip"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            data-testid={testIdPrefix ? `${testIdPrefix}-${o.value ?? 'all'}` : undefined}
          >
            {Icon && <Icon aria-hidden="true" />}
            {o.label}
            {o.count != null && <span className="adm-chip-count">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** "1-50 of 135" with previous/next. Hidden when everything fits on one page. */
export function Pager({ offset, limit, total, onChange, label = 'Pagination' }) {
  if (!total || total <= limit) return null;
  const end = Math.min(offset + limit, total);
  return (
    <nav className="adm-pager" aria-label={label}>
      <span className="adm-pager-text">
        {offset + 1}-{end} of {total}
      </span>
      <IconButton
        label="Previous page"
        icon={ChevronLeft}
        variant="outline"
        size="sm"
        disabled={offset === 0}
        onClick={() => onChange(Math.max(0, offset - limit))}
      />
      <IconButton
        label="Next page"
        icon={ChevronRight}
        variant="outline"
        size="sm"
        disabled={end >= total}
        onClick={() => onChange(offset + limit)}
      />
    </nav>
  );
}

/** The full indicator value: monospace, wraps anywhere, never truncated, with copy. */
export function IocValue({ value, type, showProblem = true }) {
  const problem = showProblem && type ? iocProblem(type, value) : null;
  return (
    <div className="adm-ioc-value-cell">
      <div className="adm-ioc-value-line">
        <code className="adm-ioc-value">{value}</code>
        <IconButton label="Copy value" icon={Copy} size="sm" onClick={() => copyText(value, 'Value')} />
      </div>
      {problem && (
        <span className="adm-ioc-problem">
          <AlertTriangle aria-hidden="true" />
          {problem}
        </span>
      )}
    </div>
  );
}

export function IocTypeBadge({ type }) {
  return <Badge size="sm" className="adm-type-badge">{iocTypeLabel(type)}</Badge>;
}

const CAMPAIGN_STATUS = {
  pending: { label: 'Pending review', tone: 'warning', icon: Clock },
  approved: { label: 'Live', tone: 'success', icon: CheckCircle2 },
  rejected: { label: 'Rejected', tone: 'neutral', icon: XCircle },
};

/** Campaign moderation state. "Live" = approved and visible to customers. */
export function CampaignStatusBadge({ status, size = 'sm' }) {
  const meta = CAMPAIGN_STATUS[status] || { label: status || 'Unknown', tone: 'neutral', icon: Clock };
  return (
    <Badge tone={meta.tone} icon={meta.icon} plain size={size}>
      {meta.label}
    </Badge>
  );
}

/** IOC review state inside a campaign. */
export function IocStatusBadge({ status }) {
  if (status === 'approved') return <Badge tone="success" plain size="sm" icon={CheckCircle2}>Published</Badge>;
  return <Badge tone="warning" plain size="sm" icon={Clock}>Pending</Badge>;
}

const SOURCE_LABEL = { otx_pulse: 'OTX pulse', csv_import: 'CSV import', manual: 'Manual', auto_scraped: 'Auto-collected' };
export const sourceLabel = (t) => SOURCE_LABEL[t] || t || 'Manual';

/** A failed refresh while older data is still on screen: say so above the data. */
export function RefreshError({ error, onRetry, what }) {
  if (!error) return null;
  return <ErrorState variant="inline" title={`Couldn't refresh ${what}; showing the last loaded data`} error={error} onRetry={onRetry} />;
}

/** Labelled form field. `children` receives the id to put on the control. */
export function Field({ label, hint, children, className = '' }) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className={`adm-field ${className}`}>
      <label htmlFor={id} className="adm-label">{label}</label>
      {children(id, hint ? hintId : undefined)}
      {hint && <p id={hintId} className="adm-hint">{hint}</p>}
    </div>
  );
}
